import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { LogOut, User, ChevronDown, Bell, RefreshCw } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useAppDispatch } from '../../store/hooks';
import { clearApiCaches } from '../../store/clearApiCaches';
import { syncMgsApi } from '../../api/syncApi';
import { notifySuccess, notifyError, roleLabel } from '../../utils';
import { SyncLoadingOverlay } from '../../shared/components/SyncLoadingOverlay';
import { ConfirmModal } from '../../shared/molecules/modal';
import MenuItems from '../../config/MenuItems';
import { PERMISSION } from '../../shared/constants/permissions';
import { NotificationDropdown } from './components/NotificationDropdown';
import { useNotifications } from './hooks/useNotifications';
import styles from './Header.module.scss';
import { MODAL_TITLES, MODAL_STRINGS, BUTTON_LABELS, ARIA_LABELS, PAGE_TITLES } from '../../shared/constants/strings';

const ROUTE_TITLES: Record<string, string> = {
  '/dashboard': 'Dashboard',
  '/clients': 'Clients',
  '/clients/create': BUTTON_LABELS.ADD_CLIENT,
  '/users': 'Users',
  '/users/create': BUTTON_LABELS.ADD_USER,
  '/integrations': 'Integrations',
  '/support-tickets': 'Support Tickets',
  '/support-tickets/create': 'Create Ticket',
  '/billing': 'Billing',
  '/event-logs': 'Event Logs',
  '/profile': 'My Profile',
  '/unauthorized': 'Unauthorized',
};

function getPageTitle(pathname: string): string {
  // Exact match first
  if (ROUTE_TITLES[pathname]) return ROUTE_TITLES[pathname];

  // Detail page patterns — e.g. /clients/abc-123 → "Client Details"
  if (/^\/clients\/.+/.test(pathname)) return 'Client Details';
  if (/^\/users\/.+/.test(pathname)) return 'User Details';
  if (/^\/integrations\/.+/.test(pathname)) return 'Integration Details';
  if (/^\/support-tickets\/.+/.test(pathname)) return 'Ticket Details';
  if (/^\/event-logs\/.+/.test(pathname)) return 'Event Details';
  if (/^\/subscriptions\/.+/.test(pathname)) return 'Subscription Details';
  if (/^\/invoices\/.+/.test(pathname)) return 'Invoice Details';

  // Fallback — find matching menu item by path prefix
  const match = MenuItems.find((item) => pathname.startsWith(item.link));
  if (match) return match.name;

  return 'Dashboard';
}

interface HeaderProps {
  sidebarExpanded?: boolean;
}

const Header: React.FC<HeaderProps> = ({ sidebarExpanded = true }) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const { user, logout, hasPermission, ownClientId } = useAuth();
  const { unreadCount } = useNotifications();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const notificationRef = useRef<HTMLDivElement>(null);

  /**
   * An account that belongs to one client reaches that client through an entry already named
   * after it, so repeating "Client Details" in the header says nothing the page has not said.
   * Only its own client qualifies — any other client page is still titled in full.
   */
  const isOwnClientPage = !!ownClientId && pathname === `/clients/${ownClientId}`;

  const title = isOwnClientPage ? PAGE_TITLES.DETAILS : getPageTitle(pathname);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (dropdownRef.current && !dropdownRef.current.contains(target)) {
        setIsDropdownOpen(false);
      }
      if (notificationRef.current && !notificationRef.current.contains(target)) {
        setIsNotificationOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogoutClick = () => {
    setIsDropdownOpen(false);
    setShowLogoutConfirm(true);
  };

  const handleLogoutConfirm = async () => {
    setShowLogoutConfirm(false);
    await logout();
  };

  const handleSync = async () => {
    if (isSyncing) return;
    setIsSyncing(true);
    try {
      await syncMgsApi();
      // The sync rewrites data behind every module, so every cached response is now suspect.
      // Awaited, so the overlay stays up until the refreshed data has actually landed rather
      // than lifting onto pre-sync values.
      await clearApiCaches(dispatch, queryClient);
      notifySuccess('Synchronization completed successfully!');
    } catch (error) {
      console.error('Sync failed:', error);
      notifyError('Failed to synchronize data. Please try again.');
    } finally {
      setIsSyncing(false);
    }
  };

  const userInitials = user ? `${user.firstName.charAt(0)}${user.lastName.charAt(0)}` : '??';

  return (
    <header className={`${styles.header} ${sidebarExpanded ? '' : styles.headerCollapsed}`}>
      <div className={styles.left}>
        <h1 className={styles.title}>{title}</h1>
      </div>

      <div className={styles.right}>
        {/* Sync Button — the same permission that gates the source configs it syncs from. */}
        {hasPermission(PERMISSION.SYNC_MANAGE) && (
          <button
            type="button"
            className={styles.syncBtn}
            onClick={handleSync}
            disabled={isSyncing}
            aria-label="Sync Data"
            data-testid="header-sync-btn"
          >
            <span className={`${styles.syncIcon} ${isSyncing ? styles.syncIconSpinning : ''}`}>
              <RefreshCw size={16} />
            </span>
            <span>{isSyncing ? 'Syncing...' : 'Sync'}</span>
          </button>
        )}

        {/* Notification Menu */}
        <div className={styles.userMenu} ref={notificationRef}>
          <button
            type="button"
            className={styles.notificationBtn}
            onClick={() => {
              setIsNotificationOpen((prev) => !prev);
              setIsDropdownOpen(false);
            }}
            aria-label="Notifications"
          >
            <Bell size={20} />
            {unreadCount > 0 && <span className={styles.badge}>{unreadCount > 99 ? '99+' : unreadCount}</span>}
          </button>
          <NotificationDropdown isOpen={isNotificationOpen} onClose={() => setIsNotificationOpen(false)} />
        </div>

        {/* User Profile Menu */}
        <div className={styles.userMenu} ref={dropdownRef}>
          <button
            className={styles.avatarBtn}
            onClick={() => {
              setIsDropdownOpen((prev) => !prev);
              setIsNotificationOpen(false);
            }}
            aria-label={ARIA_LABELS.USER_MENU}
          >
            <div className={styles.avatar}>{userInitials}</div>
            <div className={styles.userInfo}>
              <span className={styles.userName}>
                {user?.firstName} {user?.lastName}
              </span>
              <span className={styles.userRole}>{user?.role ? roleLabel(user.role) : ''}</span>
            </div>
            <ChevronDown size={16} />
          </button>

          {isDropdownOpen && (
            <div className={styles.dropdown}>
              <div className={styles.dropdownHeader}>
                <span className={styles.dropdownName}>
                  {user?.firstName} {user?.lastName}
                </span>
                <span className={styles.dropdownEmail}>{user?.email}</span>
              </div>
              <div className={styles.dropdownDivider} />
              <button
                className={styles.dropdownItem}
                onClick={() => {
                  setIsDropdownOpen(false);
                  navigate('/profile');
                }}
              >
                <User size={16} />
                <span>My Profile</span>
              </button>
              <button className={`${styles.dropdownItem} ${styles.dropdownItemDanger}`} onClick={handleLogoutClick}>
                <LogOut size={16} />
                <span>{MODAL_TITLES.SIGN_OUT}</span>
              </button>
            </div>
          )}
        </div>
      </div>

      <ConfirmModal
        isOpen={showLogoutConfirm}
        onClose={() => setShowLogoutConfirm(false)}
        onConfirm={handleLogoutConfirm}
        title={MODAL_TITLES.SIGN_OUT}
        message={MODAL_STRINGS.SIGN_OUT_CONFIRMATION}
        confirmLabel={MODAL_TITLES.SIGN_OUT}
        cancelLabel={BUTTON_LABELS.CANCEL}
        variant={MODAL_STRINGS.DANGER}
      />
      <SyncLoadingOverlay isOpen={isSyncing} />
    </header>
  );
};

export default Header;
